import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import 'ScrollToPlugin';
import VisibilitySensor from 'react-visibility-sensor';

import {
  crossScreenFontSize,
  toVw,
  toVwMobile,
  toVwTablet,
  transitionDefault,
  verticalAlign,
  verticalAlignWrapper,
} from '../../../utils/styles';
import { CONTAINER_WIDTH_SM, ITEM_TYPES } from '../../../constants';
import imagePropValidation from '../../../utils/imagePropValidation';
import mq from '@/utils/media-queries';
import caseTransitionBehavior from '../../caseTransitionBehavior';
import Media from '../../Media';

const HEIGHT = 864;
const HEIGHT_MD = 587;
const INITIAL_SCALE = 1.05;

const ContentInner = styled.div`
  opacity: 0;
  user-select: none;
  padding-left: ${toVw(15)};
  padding-right: ${toVw(15)};
  ${verticalAlign};
  ${transitionDefault};

  ${mq.tablet(css`
    padding-left: ${toVwTablet(15)};
    padding-right: ${toVwTablet(15)};
  `)};

  ${mq.mobile(css`
    padding-left: ${toVwMobile(15)};
    padding-right: ${toVwMobile(15)};
  `)};
`;

const Inner = styled.div`
  height: ${toVw(HEIGHT)};
  overflow: hidden;
  position: absolute;
  width: 100%;
  will-change: transform;
  z-index: 1;

  &:after {
    content: '';
    display: block;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    opacity: 0;
    transform: translate3d(0, 0, 0);
    ${transitionDefault};
  }

  ${mq.tablet(css`
    height: ${toVwTablet(HEIGHT_MD)};
  `)};

  ${mq.mobile(css`
    height: ${toVwMobile(CONTAINER_WIDTH_SM)};
  `)};
`;

const Wrapper = styled.div`
  position: relative;
  height: ${toVw(HEIGHT)};

  ${props =>
    props.hover &&
    css`
      ${Inner} {
        &:after {
          opacity: 1;
        }
      }

      ${ContentInner} {
        opacity: 1;
      }
    `};

  ${props =>
    props.animationRunning &&
    css`
      ${Inner} {
        &:after {
          opacity: 0;
        }
      }

      ${ContentInner} {
        opacity: 0;
      }
    `};

  ${mq.tablet(css`
    height: ${toVwTablet(HEIGHT_MD)};
  `)};

  ${mq.mobile(css`
    height: ${toVwMobile(CONTAINER_WIDTH_SM)};
  `)};
`;

const ImageMedia = styled(Media)`
  position: absolute;
  width: 100%;

  img {
    width: 100%;
    will-change: transform;

    ${props => {
      const y = props.centerVertically ? '-18%' : 0;

      return `transform: scale(${INITIAL_SCALE}) translateY(${y});`;
    }};

    ${mq.mobile(css`
      transform: scale(${INITIAL_SCALE}) translateY(0);
    `)};
  }
`;

const Content = styled.div`
  text-align: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  color: #fff;
  font-size: 0;
  z-index: 1;
  ${verticalAlignWrapper};
`;

const TitleBase = styled.div`
  ${crossScreenFontSize(52)};
`;

const Title = styled(TitleBase)`
  color: #fff;
`;

const Subtitle = styled(TitleBase)`
  color: rgba(255, 255, 255, 0.5);
`;

class PreviewBig extends React.PureComponent {
  static propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.string,
    image: imagePropValidation.isRequired,
    type: PropTypes.oneOf([ITEM_TYPES.news, ITEM_TYPES.case]).isRequired,
    slug: PropTypes.string.isRequired,
    onLinkClick: PropTypes.func.isRequired,
    isUserAgentMobile: PropTypes.func.isRequired,
    onComponentInViewport: PropTypes.func.isRequired,
    onMouseOver: PropTypes.func.isRequired,
    onMouseLeave: PropTypes.func.isRequired,
    animationRunning: PropTypes.bool,
    hover: PropTypes.bool,
    containerNode: PropTypes.func.isRequired,
    imageNode: PropTypes.func.isRequired,
    centerVertically: PropTypes.bool,
  };

  state = {
    animationRunning: false,
    hover: false,
    wrapperNode: null,
    innerNode: null,
    imageNode: null,
  };

  to = `/ru/cases/${this.props.slug}`;

  handleMouseOver = () => {
    this.props.onMouseOver();
  };

  handleMouseLeave = () => {
    this.props.onMouseLeave();
  };

  componentInViewport = isVisible => {
    if (this.props.isUserAgentMobile()) {
      this.props.onComponentInViewport(isVisible);
    }
  };

  handleLinkClick = e => {
    e.preventDefault();
    this.props.onLinkClick(this.to);
  };

  render() {
    const { title, subtitle, image, centerVertically } = this.props;

    return (
      <VisibilitySensor onChange={this.componentInViewport}>
        <a href={this.to} onClick={this.handleLinkClick}>
          <Wrapper
            animationRunning={this.props.animationRunning}
            hover={this.props.hover}
            onMouseLeave={this.handleMouseLeave}
            onMouseOver={this.handleMouseOver}
            data-link
          >
            <Inner innerRef={this.props.containerNode}>
              <ImageMedia
                type={'image'}
                content={image}
                mediaRef={this.props.imageNode}
                animationRunning={this.props.animationRunning}
                showBackground
                centerVertically={centerVertically}
              />
            </Inner>
            <Content>
              <ContentInner>
                {title && <Title>{title}</Title>}
                {subtitle && <Subtitle>{subtitle}</Subtitle>}
              </ContentInner>
            </Content>
          </Wrapper>
        </a>
      </VisibilitySensor>
    );
  }
}

export default caseTransitionBehavior(PreviewBig);



// WEBPACK FOOTER //
// ./src/components/ContentRows/components/PreviewBig.js